import React, { createRef, useEffect, useState} from 'react';
import {Button, Dropdown, Grid, Header, Icon, Popup, Segment, Tab} from "semantic-ui-react";
import {Flip, toast} from "react-toastify";
import SyntaxHighlighter from 'react-syntax-highlighter';
import {googlecode} from 'react-syntax-highlighter/dist/esm/styles/hljs';
import {getFrameworkId} from "../../utils/Methods";
import copy from "copy-to-clipboard";
import {showError} from "../../utils/ToastHelpers";
import {
    getAppListV2, getAppVersions, getAutomationCapabilities, getAutomationCapabilitiesWithAppId, getAutomationTypes
} from '../../api/apiCalls'
import { AUTOMATION_SETTINGS_HEADER, COPIED_LABEL,COPY_CODE_TO_CLIPBOARD_MESSAGE,manageAutomationSettingsMessages,NOT_FETCH_APPS } from '../../utils/UIMessages';
import { COPY_OUTLINE_ICON } from '../../utils/UiIcons';

const ManageAutomationSettings = props => {
    const [appList, setAppList] = useState([]);
    const [appVerList, setAppVerList] = useState([]);
    const [appDropdown, setAppDropdown] = useState([]);
    const [appVerDropdown, setAppVerDropdown] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedApp, setSelectedApp] = useState({});
    const [selectedAppVer, setSelectedAppVer] = useState({});
    const [loading, setLoading] = useState(true);
    const [automationFrameworks, setAutomationFrameworks] = useState([]);
    const [currentAutomationFrameworkId, setCurrentAutomationFrameworkId] = useState();
    const [currentAutomationData, setCurrentAutomationData] = useState('');

    const selectorRef = createRef();

    useEffect(() => {
        setCurrentAutomationFrameworkId(automationFrameworks[0]?.id)
    }, [automationFrameworks]);

    useEffect(() => {
        setAppDropdown(appList.map(app => {
            return {
                key: app.id,
                value: app.id,
                text: (<span>{app.name}</span>),
                image: {avatar: true, src: `data:image/png;base64,${app.icon}`}
            }
        }))
    }, [appList]);

    useEffect(() => {
        setSelectedAppVer({});
        if (selectedApp?.id) {
            getAppVersions(selectedApp.id, {page:0, size:30}).then(res => {
                setAppVerList(res.data.content);
            });
        }
    }, [selectedApp]);

    useEffect(() => {
        setAppVerDropdown(appVerList.map(appVer => {
            let appVerText = appVer.version;
            if (appVer.versionCode) {
                appVerText += ` (${appVer.versionCode})`
            }
            if (appVer.versionName) {
                appVerText += ` - ${appVer.versionName}`
            }

            return {
                key: appVer.id,
                value: appVer.id,
                text: (<span>{appVerText}{appVer.simulation ? <Icon name='syringe'/> : <></>}</span>)
            }
        }));
    }, [appVerList]);

    useEffect(() => {
        if(props.deviceId && (selectedApp?.id || selectedAppVer?.id || currentAutomationFrameworkId)){
            setLoading(true)
            let request;

            if (selectedAppVer.id) {
                request = getAutomationCapabilities(props.deviceId, selectedAppVer.id, currentAutomationFrameworkId, window.location.origin);
            } else {
                request = getAutomationCapabilitiesWithAppId(props.deviceId, selectedApp.id, currentAutomationFrameworkId, window.location.origin);
            }

            request
                .then(res => {
                    setCurrentAutomationData(res.data);
                })
                .catch(err => showError(err))
                .finally(() => setLoading(false));
        }
    }, [selectedApp, selectedAppVer, currentAutomationFrameworkId, props.deviceId]);

    useEffect(() => {
        if (props.deviceOs){
            getAppListV2({os: props.deviceOs}).then(response => {
                setAppList(response.data);
            }).catch((err) => {
                showError(`${NOT_FETCH_APPS()} ${err.response?.data?.message ?? err.toString()}`)
            })
        }
    }, [props.deviceOs])

    useEffect(() => {
        setLoading(true);
        getAutomationTypes().then(res => {
            setAutomationFrameworks(res.data)
        }).catch(() => showError(manageAutomationSettingsMessages().AUTOMATION_TYPES_EXCEPTION));
    }, []);

    const copyButtonStyle = {
        position: "absolute",
        right: 16
    }

    const getPanes = () => {
        return Object.values(automationFrameworks).map(framework => {
            return {
                menuItem: framework.name,
                render: () => <Segment className='device-manage-automation-code-segment' loading={loading}>
                    {copyButton()}
                    <div ref={selectorRef}>
                        <SyntaxHighlighter language={framework.lang} style={googlecode}>{currentAutomationData}</SyntaxHighlighter>
                    </div>
                </Segment>
            }
        })
    }

    const copyButton = () => {
        return <Popup content={COPY_CODE_TO_CLIPBOARD_MESSAGE()} position='top right'
                      trigger={<Button basic compact onClick={handleCodeCopy} icon={COPY_OUTLINE_ICON}
                                       style={copyButtonStyle}/>}/>
    }

    const handleCodeCopy = () => {
        copy(selectorRef.current.innerText);
        toast.info(COPIED_LABEL(), {
            autoClose: 1200,
            transition: Flip
        })
    };

    const onChangeAppDropdown = (e, {value}) => {
        const appId = value
        const selectedApp = appList.find(app => app.id === appId)
        setSelectedApp(selectedApp);
    }

    const onChangeAppVerDropdown = (e, {value}) => {
        const appVerId = value
        const selectedAppVer = appVerList.find(appVer => appVer.id === appVerId)
        setSelectedAppVer(selectedAppVer);
    }

    const handleTabChange = (e, {activeIndex}) => {
        setActiveIndex(activeIndex);
        setCurrentAutomationFrameworkId(getFrameworkId(automationFrameworks, e.target.innerHTML));
    }

    return (
        <Grid columns={1}>
            <Grid.Column width={16}>
                <Grid.Row className='device-manage-automation-row-padding-top'>
                    <Grid centered>
                        <Grid.Row centered columns={2}
                                  className='device-manage-automation-row-padding-top'  // cok bitisik oluyor
                        >
                            <Grid.Column width={4}>
                                <Header as='h4' dividing style={{marginTop:10, marginLeft:2}}>
                                    {AUTOMATION_SETTINGS_HEADER()}
                                </Header>
                            </Grid.Column>
                            <Grid.Column width={6}>
                                <Dropdown selection fluid options={appDropdown} className='device-manage-automation-select-app-dropdown'
                                          onChange={onChangeAppDropdown}
                                          placeholder={manageAutomationSettingsMessages().DROPDOWN_PLACEHOLDER}/>
                            </Grid.Column>
                            <Grid.Column width={6}>
                                    { selectedApp.id && <Dropdown selection fluid options={appVerDropdown} className='device-manage-automation-select-app-dropdown'
                                          onChange={onChangeAppVerDropdown}
                                          placeholder={manageAutomationSettingsMessages().SELECT_APPLICATION_VERSION}/> }
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Grid.Row>
                <Grid.Row className='device-manage-automation-row-padding-top'>
                    <Tab menu={{secondary: true, fluid: true, vertical: true}} panes={getPanes()}
                         style={{fontFamily: "monospace"}}
                         activeIndex={activeIndex}
                         onTabChange={handleTabChange}
                    />
                </Grid.Row>
            </Grid.Column>
        </Grid>
    );
}

export default ManageAutomationSettings;